import { Shield, AlertTriangle, Smartphone, Loader2, ArrowRight, KeyRound, Check, Copy, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Mandatory 2FA Setup Component
 * Handles the complete 2FA setup flow: init -> QR code -> verify -> backup codes
 */
export function TwoFactorSetup({
  setupStep,
  setSetupStep,
  setupUser,
  setupData,
  verifyCode,
  setVerifyCode,
  backupCodes,
  loading,
  error,
  copiedIndex,
  onStartSetup,
  onVerifySetup,
  onSetupComplete,
  onCopyToClipboard,
  onCopyAllBackupCodes,
  onResetAll
}) {
  return (
    <>
      {/* Logo and title for setup */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-amber-500 to-orange-600">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          {setupStep === 'backup' ? 'Lagre backup-koder' : 'Aktiver to-faktor'}
        </h1>
        <p className="text-slate-600">
          {setupStep === 'init' && `Hei ${setupUser?.name || ''}, du må aktivere 2FA for å fortsette`}
          {setupStep === 'qr' && 'Skann QR-koden med en autentiseringsapp'}
          {setupStep === 'verify' && 'Skriv inn koden fra appen din'}
          {setupStep === 'backup' && 'Lagre disse kodene på et trygt sted'}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Setup Step: Init */}
      {setupStep === 'init' && (
        <SetupInitStep
          loading={loading}
          onStartSetup={onStartSetup}
          onResetAll={onResetAll}
        />
      )}

      {/* Setup Step: QR Code */}
      {setupStep === 'qr' && setupData && (
        <SetupQRStep
          setupData={setupData}
          copiedIndex={copiedIndex}
          onCopyToClipboard={onCopyToClipboard}
          onNext={() => setSetupStep('verify')}
        />
      )}

      {/* Setup Step: Verify */}
      {setupStep === 'verify' && (
        <SetupVerifyStep
          verifyCode={verifyCode}
          setVerifyCode={setVerifyCode}
          loading={loading}
          onVerifySetup={onVerifySetup}
          onBack={() => setSetupStep('qr')}
        />
      )}

      {/* Setup Step: Backup Codes */}
      {setupStep === 'backup' && (
        <SetupBackupStep
          backupCodes={backupCodes}
          copiedIndex={copiedIndex}
          onCopyToClipboard={onCopyToClipboard}
          onCopyAllBackupCodes={onCopyAllBackupCodes}
          onSetupComplete={onSetupComplete}
        />
      )}
    </>
  );
}

function SetupInitStep({ loading, onStartSetup, onResetAll }) {
  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center space-x-2 text-amber-700">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">2FA er påkrevd</span>
        </div>
        <p className="text-sm text-amber-600 mt-2">
          For å beskytte kontoen din og systemet vårt, krever vi at alle brukere aktiverer to-faktor autentisering.
        </p>
      </div>

      <div className="bg-slate-50 rounded-xl p-4">
        <h4 className="font-medium text-slate-900 mb-2">Du trenger:</h4>
        <ul className="text-sm text-slate-600 space-y-2">
          <li className="flex items-center space-x-2">
            <Smartphone className="w-4 h-4 text-teal-500" />
            <span>En autentiseringsapp (Google Authenticator, Microsoft Authenticator, etc.)</span>
          </li>
        </ul>
      </div>

      <Button
        onClick={onStartSetup}
        disabled={loading}
        className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
      >
        {loading ? (
          <div className="flex items-center justify-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Forbereder...</span>
          </div>
        ) : (
          <>
            <Shield className="mr-2 w-5 h-5" />
            Start oppsett
          </>
        )}
      </Button>

      <button
        type="button"
        onClick={onResetAll}
        className="w-full text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors py-2"
      >
        ← Bruk en annen konto
      </button>
    </div>
  );
}

function SetupQRStep({ setupData, copiedIndex, onCopyToClipboard, onNext }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <img 
          src={setupData.qr_code} 
          alt="2FA QR Code" 
          className="w-48 h-48 border-2 border-slate-200 rounded-xl"
        />
      </div>

      <div className="text-center">
        <p className="text-sm text-slate-500 mb-2">Kan ikke skanne? Bruk denne koden:</p>
        <div className="flex items-center justify-center space-x-2">
          <code className="bg-slate-100 px-3 py-2 rounded-lg text-sm font-mono break-all">
            {setupData.manual_entry_key}
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCopyToClipboard(setupData.manual_entry_key, 'manual')}
          >
            {copiedIndex === 'manual' ? (
              <Check className="w-4 h-4 text-emerald-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      <Button
        onClick={onNext}
        className="w-full h-14 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
      >
        Neste
        <ArrowRight className="ml-2 w-5 h-5" />
      </Button>
    </div>
  );
}

function SetupVerifyStep({ verifyCode, setVerifyCode, loading, onVerifySetup, onBack }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Verifiseringskode</Label>
        <div className="relative">
          <KeyRound className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value)}
            placeholder="000 000"
            className="pl-12 h-14 text-center text-2xl tracking-widest rounded-xl border-2 border-slate-200 focus:border-teal-500 transition-colors"
            disabled={loading}
            maxLength={7}
            autoFocus
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Skriv inn den 6-sifrede koden fra autentiseringsappen din
        </p>
      </div>

      <div className="flex space-x-3">
        <Button
          onClick={onBack}
          variant="outline"
          className="flex-1 h-12 rounded-xl"
        >
          Tilbake
        </Button>
        <Button
          onClick={onVerifySetup}
          disabled={loading || verifyCode.replace(/\s/g, '').length < 6}
          className="flex-1 h-12 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white rounded-xl"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Verifiser'
          )}
        </Button>
      </div>
    </div>
  );
}

function SetupBackupStep({ backupCodes, copiedIndex, onCopyToClipboard, onCopyAllBackupCodes, onSetupComplete }) {
  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center space-x-2 text-amber-700 mb-2">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Viktig!</span>
        </div>
        <p className="text-sm text-amber-600">
          Lagre disse kodene på et trygt sted. Du trenger dem hvis du mister tilgang til autentiseringsappen.
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        {backupCodes.map((code, i) => (
          <div 
            key={i}
            className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg font-mono text-sm"
          >
            <span>{code}</span>
            <button
              onClick={() => onCopyToClipboard(code, i)}
              className="p-1 hover:bg-slate-200 rounded"
            >
              {copiedIndex === i ? (
                <Check className="w-3 h-3 text-emerald-500" />
              ) : (
                <Copy className="w-3 h-3 text-slate-400" />
              )}
            </button>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        className="w-full h-12 rounded-xl"
        onClick={onCopyAllBackupCodes}
      >
        {copiedIndex === 'all' ? (
          <>
            <Check className="w-4 h-4 mr-2 text-emerald-500" />
            Kopiert!
          </>
        ) : (
          <>
            <Copy className="w-4 h-4 mr-2" />
            Kopier alle koder
          </>
        )}
      </Button>

      <Button
        onClick={onSetupComplete}
        className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
      >
        <Check className="mr-2 w-5 h-5" />
        Ferdig - Logg inn nå
      </Button>
    </div>
  );
}
